/**
 * WordPress dependencies
 */
import { renderToString, useEffect, useMemo, useReducer, useRef } from '@wordpress/element';
import { useInstanceId, useMergeRefs, useRefEffect } from '@wordpress/compose';
import { create, slice, insert, isCollapsed, getTextContent, privateApis as richTextPrivateApis } from '@wordpress/rich-text';
import { speak } from '@wordpress/a11y';
import { isAppleOS } from '@wordpress/keycodes';

/**
 * Internal dependencies
 */
import { AutocompleterUI } from './autocompleter-ui';
import { getAutocompleteMatch } from './get-autocomplete-match';
import { withIgnoreIMEEvents } from '../utils/with-ignore-ime-events';
import getNodeText from '../utils/get-node-text';
import { unlock } from '../lock-unlock';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const {
  subscribeOwnedListener
} = unlock(richTextPrivateApis);
const EMPTY_FILTERED_OPTIONS = [];

// Used for generating the instance ID
const AUTOCOMPLETE_HOOK_REFERENCE = {};
function getCompletionObject(completion) {
  if (completion !== null && typeof completion === 'object' && 'action' in completion && completion.action !== undefined && 'value' in completion && completion.value !== undefined) {
    return completion;
  }
  return {
    action: 'insert-at-caret',
    value: completion
  };
}
const initialState = {
  selectedIndex: 0,
  filteredOptions: EMPTY_FILTERED_OPTIONS,
  filterValue: '',
  autocompleter: null
};
function autocompleteReducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return initialState;
    case 'SELECT':
      return {
        ...state,
        selectedIndex: action.index
      };
    case 'OPTIONS':
      return {
        ...state,
        filteredOptions: action.options,
        selectedIndex: action.options.length === state.filteredOptions.length ? state.selectedIndex : 0
      };
    case 'MATCH':
      return {
        ...state,
        autocompleter: action.completer,
        filterValue: action.query
      };
  }
}
export function useAutocomplete({
  record,
  onChange,
  onReplace,
  completers,
  contentRef
}) {
  const instanceId = useInstanceId(AUTOCOMPLETE_HOOK_REFERENCE);
  const [state, dispatch] = useReducer(autocompleteReducer, initialState);
  const {
    selectedIndex,
    filteredOptions,
    filterValue,
    autocompleter
  } = state;
  const backspacingRef = useRef(false);
  const prevRecordTextRef = useRef('');
  const lastCompletionRef = useRef(null);
  function insertCompletion(replacement) {
    if (autocompleter === null) {
      return '';
    }
    const end = record.start;
    const start = end - autocompleter.triggerPrefix.length - filterValue.length;
    const toInsert = create({
      html: renderToString(replacement)
    });
    onChange(insert(record, toInsert, start, end));
    return getTextContent(toInsert);
  }
  function select(option) {
    if (option.isDisabled || !autocompleter) {
      return;
    }
    const {
      getOptionCompletion
    } = autocompleter;
    if (!getOptionCompletion) {
      dispatch({
        type: 'RESET'
      });
      contentRef.current?.focus();
      return;
    }
    const completionObject = getCompletionObject(getOptionCompletion(option.value, filterValue));
    if ('replace' === completionObject.action) {
      onReplace([completionObject.value]);
      // When replacing, the component will unmount, so don't reset
      // state (below) on an unmounted component.
      return;
    }
    if ('insert-at-caret' === completionObject.action) {
      const completionText = insertCompletion(completionObject.value);
      // When the completion value starts with the trigger prefix
      // (e.g. @username), the trigger stays in the text and would
      // re-activate the autocompleter. Store the completed text so
      // the effect can suppress the stale re-match.
      if (completionText.startsWith(autocompleter.triggerPrefix)) {
        const afterPrefix = completionText.slice(autocompleter.triggerPrefix.length);
        if (afterPrefix) {
          lastCompletionRef.current = {
            name: autocompleter.name,
            value: afterPrefix
          };
        }
      }
    }

    // Reset autocomplete state after insertion rather than before
    // so insertion events don't cause the completion menu to redisplay.
    dispatch({
      type: 'RESET'
    });

    // Make sure that the content remains focused after making a selection
    // and that the text cursor position is not lost.
    contentRef.current?.focus();
  }
  function onChangeOptions(options) {
    dispatch({
      type: 'OPTIONS',
      options
    });
  }
  function handleKeyDown(event) {
    backspacingRef.current = event.key === 'Backspace';
    if (!autocompleter) {
      return;
    }
    if (filteredOptions.length === 0) {
      return;
    }
    if (event.defaultPrevented) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        {
          const offset = event.key === 'ArrowUp' ? -1 : 1;
          const newIndex = (selectedIndex + offset + filteredOptions.length) % filteredOptions.length;
          dispatch({
            type: 'SELECT',
            index: newIndex
          });
          // See the related PR as to why this is necessary: https://github.com/WordPress/gutenberg/pull/54902.
          if (isAppleOS()) {
            speak(getNodeText(filteredOptions[newIndex].label), 'assertive');
          }
          break;
        }
      case 'Escape':
        dispatch({
          type: 'RESET'
        });
        event.preventDefault();
        break;
      case 'Enter':
        select(filteredOptions[selectedIndex]);
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        dispatch({
          type: 'RESET'
        });
        return;
      default:
        return;
    }

    // Any handled key should prevent original behavior. This relies on
    // the early return in the default case.
    event.preventDefault();
  }

  // textContent is a primitive (string), memoizing is not strictly necessary
  // but this is a preemptive performance improvement, since the autocompleter
  // is a potential bottleneck for the editor type metric.
  const textContent = useMemo(() => {
    if (isCollapsed(record)) {
      return getTextContent(slice(record, 0));
    }
    return '';
  }, [record]);
  useEffect(() => {
    const isTextChange = record.text !== prevRecordTextRef.current;
    prevRecordTextRef.current = record.text;
    function getTextAfterSelection() {
      return textContent ? getTextContent(slice(record, undefined, getTextContent(record).length)) : '';
    }
    const match = getAutocompleteMatch(textContent, completers, {
      matchCount: filteredOptions.length,
      isBackspacing: backspacingRef.current,
      getTextAfterSelection,
      lastCompletion: lastCompletionRef.current
    });
    if (!match) {
      if (autocompleter) {
        dispatch({
          type: 'RESET'
        });
      }
      return;
    }
    const {
      completer,
      filterValue: query
    } = match;

    // Don't re-activate a dismissed autocompleter on cursor-only
    // movement. `textContent` (text before cursor) changes with the
    // caret, so the effect re-runs, but `record.text` does not.
    // Complements the render-time `didUserInput` gate in
    // `useAutocompleteProps` for callers using this hook directly.
    if (!autocompleter && !isTextChange) {
      return;
    }

    // Clear stale completion ref when the user types a new trigger
    // for the same completer (the previous completion is no longer
    // relevant). Must be after the cursor-only check so that mere
    // cursor movement doesn't discard the suppression state.
    if (lastCompletionRef.current && lastCompletionRef.current.name === completer.name) {
      lastCompletionRef.current = null;
    }
    dispatch({
      type: 'MATCH',
      completer,
      query
    });
    // We want to avoid introducing unexpected side effects.
    // See https://github.com/WordPress/gutenberg/pull/41820
  }, [textContent]);
  const {
    key: selectedKey = ''
  } = filteredOptions[selectedIndex] || {};
  const {
    className
  } = autocompleter || {};
  const isExpanded = !!autocompleter && filteredOptions.length > 0;
  const listBoxId = isExpanded ? `components-autocomplete-listbox-${instanceId}` : undefined;
  const activeId = isExpanded ? `components-autocomplete-item-${instanceId}-${selectedKey}` : null;
  const hasSelection = record.start !== undefined;
  const showPopover = !!textContent && hasSelection && !!autocompleter;
  return {
    listBoxId,
    activeId,
    onKeyDown: withIgnoreIMEEvents(handleKeyDown),
    popover: showPopover && /*#__PURE__*/_jsx(AutocompleterUI, {
      autocompleter: autocompleter,
      className: className,
      filterValue: filterValue,
      instanceId: instanceId,
      listBoxId: listBoxId,
      selectedIndex: selectedIndex,
      onChangeOptions: onChangeOptions,
      onSelect: select,
      contentRef: contentRef,
      reset: () => dispatch({
        type: 'RESET'
      })
    }, autocompleter.name + autocompleter.triggerPrefix)
  };
}

/**
 * Checks whether two records represent the same user-visible state
 * (same text content and cursor position).
 */
function recordValuesMatch(a, b) {
  return a.text === b.text && a.start === b.start && a.end === b.end;
}

/**
 * Tracks the last record whose value differed from the current one.
 * Used to determine whether the user has actually typed something
 */
export function useLastDifferentValue(value) {
  const history = useRef([]);
  const lastEntry = history.current[history.current.length - 1];

  // Only add to history if the value is meaningfully different from
  // the most recent entry (analogous to Set.add being a no-op for
  // duplicate references in the original implementation).
  if (!lastEntry || !recordValuesMatch(value, lastEntry)) {
    history.current.push(value);
  }

  // Keep the history size to 2.
  if (history.current.length > 2) {
    history.current.shift();
  }
  return history.current[0];
}

// The popover is anchored to the element this hook's own `ref` lands on, so it
// owns `contentRef` and callers don't provide one.
export function useAutocompleteProps(options) {
  const ref = useRef(null);
  const onKeyDownRef = useRef(undefined);
  const {
    record
  } = options;
  const previousRecord = useLastDifferentValue(record);
  const {
    popover,
    listBoxId,
    activeId,
    onKeyDown
  } = useAutocomplete({
    ...options,
    contentRef: ref
  });
  onKeyDownRef.current = onKeyDown;
  const mergedRefs = useMergeRefs([ref, useRefEffect(element => {
    function _onKeyDown(event) {
      onKeyDownRef.current?.(event);
    }
    // Capture phase. When the autocomplete popover is open,
    // Up/Down/Enter/Escape must navigate the completion list —
    // they shouldn't be consumed by ancestor handlers (e.g.
    // block-editor's writing-flow) for block navigation, block
    // splitting, or "move out of parent" actions. Those handlers
    // fire at bubble phase and gate on `event.defaultPrevented`,
    // so firing in capture lets us preventDefault first when the
    // popover is active.
    return subscribeOwnedListener(element, 'keydown', _onKeyDown, true);
  }, [])]);

  // We only want to show the popover if the user has typed something.
  const didUserInput = record.text !== previousRecord?.text;
  if (!didUserInput) {
    return {
      ref: mergedRefs
    };
  }

  // `aria-owns` and `aria-controls` point at the same list: either one lets
  // `aria-activedescendant` resolve to an option rendered outside this
  // element, and assistive technology support differs between them.
  //
  // No `aria-expanded`: consumers are `textbox` elements, which don't
  // support it; that would mean `role="combobox"`, which in turn doesn't
  // support the `aria-multiline` these fields set.
  return {
    ref: mergedRefs,
    children: popover,
    'aria-autocomplete': listBoxId ? 'list' : undefined,
    'aria-haspopup': listBoxId ? 'listbox' : undefined,
    'aria-controls': listBoxId,
    'aria-owns': listBoxId,
    'aria-activedescendant': activeId ?? undefined
  };
}
export default function Autocomplete({
  children,
  isSelected,
  ...options
}) {
  const {
    popover,
    ...props
  } = useAutocomplete(options);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [children(props), isSelected && popover]
  });
}